import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../../firebase/config';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import { formatEuroCents } from '../../utils/festpreisAngebot';

const euro = (cents) => `${formatEuroCents(Math.round(Number(cents) || 0))} €`;

/**
 * Zentrale ändert bei einer Festpreisfahrt das Ziel oder meldet den früheren
 * Ausstieg (Phase 3a, E8/E13/E14). Gerechnet wird auf dem Server
 * (`requoteRide`, dieselbe Rechnung wie in der Kunden-App):
 * - vor dem Einsteigen: neuer Festpreis ab Abholort,
 * - während der Fahrt: bisher gefahren + Rest + Zielwechsel-Gebühr,
 * - früherer Ausstieg: bisher gefahren, innerhalb 500 m voller Preis.
 * Zweistufig: erst berechnen und zeigen, dann mit `confirm` übernehmen.
 * Der Fahrer bekommt den Hinweis „Ziel geändert“, der Fahrgast eine Push-Nachricht.
 */
const km = (v) => Number(v || 0).toFixed(1).replace('.', ',');

const FestpreisZielwechselDialog = ({ open, ride, art = 'zielwechsel', onClose, onDone }) => {
  const [destination, setDestination] = useState({ address: '', location: null });
  const [angebot, setAngebot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const vorzeitig = art === 'vorzeitig';

  useEffect(() => {
    if (!open) return;
    setDestination({ address: '', location: null });
    setAngebot(null);
    setError(null);
    setLoading(false);
    setSaving(false);
  }, [open, art]);

  const berechnen = async (dest) => {
    setLoading(true);
    setError(null);
    setAngebot(null);
    try {
      const payload = vorzeitig
        ? { rideId: ride.id, art: 'vorzeitig' }
        : {
            rideId: ride.id,
            destination: {
              lat: dest.location.latitude,
              lng: dest.location.longitude,
              address: dest.address,
              ...(dest.location.placeId ? { placeId: dest.location.placeId } : {}),
            },
          };
      const res = await httpsCallable(functions, 'requoteRide')(payload);
      setAngebot(res.data);
    } catch (e) {
      setError(e?.message || 'Der Preis konnte nicht berechnet werden.');
    } finally {
      setLoading(false);
    }
  };

  // Früherer Ausstieg: sofort beim Öffnen rechnen (keine Eingabe nötig).
  useEffect(() => {
    if (open && vorzeitig && ride?.id) berechnen(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vorzeitig, ride?.id]);

  const uebernehmen = async () => {
    setSaving(true);
    setError(null);
    try {
      await httpsCallable(functions, 'requoteRide')({ rideId: ride.id, quoteId: angebot.quoteId, confirm: true });
      onDone?.(angebot);
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Die Änderung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const waehrend = angebot?.phase === 'waehrend_fahrt';
  const kannUebernehmen = !!angebot?.quoteId && !angebot?.imPuffer && !angebot?.paypalBlockiert && !saving;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      // Vorschlagsliste der Adresssuche darf über den Dialog hinausragen.
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogTitle>{vorzeitig ? 'Fahrgast ausgestiegen' : 'Ziel ändern'}</DialogTitle>
      <DialogContent sx={{ overflow: 'visible' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {vorzeitig
            ? 'Der Fahrgast steigt am aktuellen Standort des Fahrzeugs aus. Es zählt der bisher gefahrene Anteil des Festpreises, ohne Gebühr.'
            : ride?.status === 'in_progress'
              ? 'Die Fahrt läuft: Bisher gefahrener Anteil + Strecke ab hier + Gebühr für den Zielwechsel.'
              : 'Noch nicht eingestiegen: Der Festpreis wird ab dem Abholort neu berechnet.'}
          {' '}Der Fahrgast hat telefonisch zugestimmt.
        </Typography>

        {!vorzeitig && (
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <PlaceAutocomplete
              label="Neues Ziel"
              subDestinationRole="destination"
              requireLocation
              location={destination.location}
              value={destination.address}
              onChange={(e) => {
                const address = e.target.value;
                setDestination((s) => ({ address, location: address === s.address ? s.location : null }));
                setAngebot(null);
              }}
              onPlaceSelect={(address, location) => {
                const next = { address, location };
                setDestination(next);
                berechnen(next);
              }}
              size="small"
              fullWidth
            />
          </APIProvider>
        )}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Wird berechnet …</Typography>
          </Box>
        )}

        {angebot && (
          <Box sx={{ mt: 2 }}>
            {angebot.imPuffer ? (
              <Alert severity="info">
                Das Fahrzeug ist weniger als 500 m vom Ziel entfernt. Es bleibt der vereinbarte Festpreis
                von {euro(angebot.alteCents)}.
              </Alert>
            ) : (
              <>
                {waehrend && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      Bisher gefahren ({km(angebot.gefahrenKm)} km): {euro(angebot.angerechnetCents)}
                    </Typography>
                    {!vorzeitig && (
                      <Typography variant="body2">
                        Ab hier {km(angebot.restKm)} km: {euro(angebot.restCents)}
                      </Typography>
                    )}
                    {!vorzeitig && Number(angebot.gebuehrCents) > 0 && (
                      <Typography variant="body2">
                        Gebühr für Zielwechsel: {euro(angebot.gebuehrCents)}
                      </Typography>
                    )}
                    <Divider sx={{ my: 1 }} />
                  </Box>
                )}
                {!waehrend && angebot.distanceKm != null && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Abholort → neues Ziel: {km(angebot.distanceKm)} km
                    {angebot.durationMin > 0 ? ` · ${angebot.durationMin} min` : ''}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="body2" color="text.secondary">
                    bisher {euro(angebot.alteCents)}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Neuer Festpreis {euro(angebot.neueCents)}
                  </Typography>
                </Box>
                {angebot.paypalBlockiert && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Der neue Preis übersteigt die PayPal-Reservierung. Bitte zuerst auf Barzahlung umstellen.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        {vorzeitig && angebot && !loading && (
          <Button onClick={() => berechnen(null)} disabled={saving}>Neu berechnen</Button>
        )}
        <Button
          variant="contained"
          onClick={uebernehmen}
          disabled={!kannUebernehmen}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FestpreisZielwechselDialog;
